import type { CSSProperties, FC, ReactNode } from 'react'
import type { Product } from '@/components/riffle/types'

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react'
import gsap from 'gsap'

import { IMG_GRADE, cardUrl, hiresUrl } from '@/components/riffle/data'

interface DetailViewProps {
    product: Product
    index: number
    total: number
    origin: { x: number; y: number; w: number; h: number }
    reducedMotion: boolean
    onClose: () => void
}

// §16.2 — the clip-path inset that frames the clicked card at t=0 and the
// full viewport at t=1 (corners squaring from 16px → 0).
const clipInset = (
    o: { x: number; y: number; w: number; h: number },
    t: number
): string => {
    const k = 1 - t
    const W = window.innerWidth
    const H = window.innerHeight
    const top = o.y * k
    const right = (W - (o.x + o.w)) * k
    const bottom = (H - (o.y + o.h)) * k
    const left = o.x * k
    return `inset(${top}px ${right}px ${bottom}px ${left}px round ${16 * k}px)`
}

const DetailView: FC<DetailViewProps> = ({
    product,
    index,
    total,
    origin,
    reducedMotion,
    onClose
}): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const bdRef = useRef<HTMLDivElement>(null)
    const heroRef = useRef<HTMLDivElement>(null)
    const kbbgRef = useRef<HTMLImageElement>(null)
    const kbRef = useRef<HTMLImageElement>(null)
    const scrimRef = useRef<HTMLDivElement>(null)
    const shineRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLSpanElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const orderRef = useRef<HTMLButtonElement>(null)

    // gsap instances to kill on close / unmount
    const clipTween = useRef<gsap.core.Tween | null>(null)
    const tweens = useRef<gsap.core.Animation[]>([])
    const closing = useRef(false)

    const [ordered, setOrdered] = useState(false)
    const orderTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const nn = String(index + 1).padStart(2, '0')

    // ─── Open ──────────────────────────────────────────────
    useLayoutEffect(() => {
        const o = origin
        const hero = heroRef.current
        const revs = rootRef.current
            ? Array.from(rootRef.current.querySelectorAll('[data-rev]'))
            : []

        // initial states (set before paint)
        gsap.set(rootRef.current, { opacity: 1 })
        gsap.set(bdRef.current, { opacity: 0 })
        if (hero) hero.style.clipPath = clipInset(o, 0)
        gsap.set(kbRef.current, { scale: 1, opacity: 1 })
        gsap.set(kbbgRef.current, { scale: 1.12 })
        gsap.set(scrimRef.current, { opacity: 0 })
        gsap.set(shineRef.current, { opacity: 0, xPercent: -170 })
        gsap.set(titleRef.current, { yPercent: 120 })
        gsap.set(revs, { opacity: 0, y: 24 })
        gsap.set(closeRef.current, { opacity: 0, scale: 0.4, rotate: -90 })
        gsap.set(orderRef.current, { opacity: 0 })

        if (reducedMotion) {
            gsap.set(bdRef.current, { opacity: 1 })
            if (hero) hero.style.clipPath = clipInset(o, 1)
            gsap.set(scrimRef.current, { opacity: 1 })
            gsap.set(titleRef.current, { yPercent: 0 })
            gsap.set(revs, { opacity: 1, y: 0 })
            gsap.set(closeRef.current, { opacity: 1, scale: 1, rotate: 0 })
            gsap.set(orderRef.current, { opacity: 1 })
            return
        }

        const list: gsap.core.Animation[] = []
        const proxy = { t: 0 }

        list.push(
            gsap.fromTo(
                bdRef.current,
                { opacity: 0 },
                { opacity: 1, duration: 0.4, ease: 'power2.out', overwrite: 'auto' }
            )
        )

        clipTween.current = gsap.to(proxy, {
            t: 1,
            duration: 0.95,
            ease: 'power3.inOut',
            overwrite: 'auto',
            onUpdate: () => {
                if (hero) hero.style.clipPath = clipInset(o, proxy.t)
            }
        })
        list.push(clipTween.current)

        // slow drift on the blurred fill only
        list.push(
            gsap.fromTo(
                kbbgRef.current,
                { scale: 1.12 },
                { scale: 1.24, duration: 13, ease: 'none', overwrite: 'auto' }
            )
        )

        list.push(
            gsap.fromTo(
                scrimRef.current,
                { opacity: 0 },
                { opacity: 1, duration: 0.7, delay: 0.25, ease: 'power2.out', overwrite: 'auto' }
            )
        )

        const shineTl = gsap.timeline({ delay: 0.5 })
        shineTl
            .fromTo(shineRef.current, { xPercent: -170, opacity: 0 }, { opacity: 1, duration: 0.18 })
            .to(shineRef.current, { xPercent: 170, duration: 0.95, ease: 'power2.inOut' })
            .to(shineRef.current, { opacity: 0, duration: 0.25 }, 0.8)
        list.push(shineTl)

        list.push(
            gsap.fromTo(
                titleRef.current,
                { yPercent: 120 },
                { yPercent: 0, duration: 0.9, ease: 'power4.out', delay: 0.46, overwrite: 'auto' }
            )
        )

        list.push(
            gsap.fromTo(
                revs,
                { opacity: 0, y: 24 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.7,
                    ease: 'power4.out',
                    stagger: 0.08,
                    delay: 0.56,
                    overwrite: 'auto'
                }
            )
        )

        list.push(
            gsap.fromTo(
                closeRef.current,
                { opacity: 0, scale: 0.4, rotate: -90 },
                { opacity: 1, scale: 1, rotate: 0, duration: 0.5, ease: 'back.out(2)', delay: 0.55, overwrite: 'auto' }
            )
        )

        // §16.4 — Order button fades in with OPACITY ONLY (never a GSAP transform).
        list.push(
            gsap.fromTo(
                orderRef.current,
                { opacity: 0 },
                { opacity: 1, duration: 0.55, ease: 'power2.out', delay: 0.98, overwrite: 'auto' }
            )
        )

        tweens.current = list

        return () => {
            list.forEach((t) => t.kill())
            clipTween.current?.kill()
        }
        // run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ─── Close (mirror of open) ────────────────────────────
    const close = useCallback(() => {
        if (closing.current) return
        closing.current = true

        // kill any in-flight animations first (the "glitch" guard)
        clipTween.current?.kill()
        tweens.current.forEach((t) => t.kill())

        const hero = heroRef.current
        const revs = rootRef.current
            ? Array.from(rootRef.current.querySelectorAll('[data-rev]'))
            : []

        if (reducedMotion) {
            onClose()
            return
        }

        const o = origin
        const proxy = { t: 1 }

        gsap.to(revs, { opacity: 0, y: 14, duration: 0.26, ease: 'power2.in', stagger: 0.03, overwrite: 'auto' })
        gsap.to(orderRef.current, { opacity: 0, duration: 0.24, ease: 'power2.in', overwrite: 'auto' })
        gsap.to(titleRef.current, { yPercent: 120, duration: 0.34, ease: 'power3.in', overwrite: 'auto' })
        gsap.to(closeRef.current, { opacity: 0, scale: 0.5, rotate: 90, duration: 0.22, ease: 'power2.in', overwrite: 'auto' })
        gsap.to(scrimRef.current, { opacity: 0, duration: 0.45, ease: 'power2.in', overwrite: 'auto' })
        gsap.to(bdRef.current, { opacity: 0, duration: 0.6, delay: 0.12, ease: 'power2.inOut', overwrite: 'auto' })

        clipTween.current = gsap.to(proxy, {
            t: 0,
            duration: 0.8,
            delay: 0.05,
            ease: 'power3.inOut',
            overwrite: 'auto',
            onUpdate: () => {
                if (hero) hero.style.clipPath = clipInset(o, proxy.t)
            },
            onComplete: onClose
        })

        // dissolve the hero so the handoff to the small deck card never pops
        gsap.to(heroRef.current, { opacity: 0, duration: 0.34, delay: 0.52, ease: 'power2.in', overwrite: 'auto' })
    }, [origin, reducedMotion, onClose])

    // Escape closes
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [close])

    useEffect(() => {
        return () => {
            if (orderTimer.current) clearTimeout(orderTimer.current)
        }
    }, [])

    const onKbLoad = (e: React.SyntheticEvent<HTMLImageElement>): void => {
        const img = e.currentTarget
        img.style.objectFit =
            img.naturalHeight / img.naturalWidth > 1.2 ? 'contain' : 'cover'
    }

    const onOrder = (): void => {
        if (ordered) return
        setOrdered(true)
        orderTimer.current = setTimeout(() => setOrdered(false), 2200)
    }

    const orderBg = ordered ? 'rgba(152,212,162,0.97)' : 'rgba(255,255,255,0.97)'
    const orderShadow = ordered
        ? '0 14px 36px -12px rgba(120,200,140,0.65)'
        : '0 14px 36px -12px rgba(0,0,0,0.65)'

    const revStyle: CSSProperties = { willChange: 'transform, opacity' }

    return (
        <div
            ref={rootRef}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 50,
                color: 'rgba(255,255,255,0.97)',
                touchAction: 'none',
                opacity: 0
            }}
        >
            {/* 1 · black plate */}
            <div
                ref={bdRef}
                data-bd
                style={{ position: 'absolute', inset: 0, background: '#050507' }}
            />

            {/* 2 · hero (clip-path unfold) */}
            <div
                ref={heroRef}
                data-hero
                style={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    willChange: 'clip-path',
                    clipPath: 'inset(50%)'
                }}
            >
                <img
                    ref={kbbgRef}
                    data-kbbg
                    src={cardUrl(index + 1)}
                    alt=''
                    aria-hidden
                    draggable={false}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: 'scale(1.12)',
                        filter: `blur(34px) ${IMG_GRADE}`,
                        opacity: 0.5
                    }}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,8,0.42)' }} />
                <img
                    ref={kbRef}
                    data-kb
                    src={hiresUrl(index + 1)}
                    onLoad={onKbLoad}
                    alt={`${product.title} — ${product.artist}`}
                    draggable={false}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: IMG_GRADE
                    }}
                />
                <div
                    ref={scrimRef}
                    data-scrim
                    style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        background:
                            'linear-gradient(to top, rgba(4,4,7,0.95) 0%, rgba(4,4,7,0.72) 16%, rgba(4,4,7,0.18) 40%, rgba(4,4,7,0) 64%), linear-gradient(to right, rgba(4,4,7,0.55) 0%, rgba(4,4,7,0) 42%)'
                    }}
                />
                <div
                    ref={shineRef}
                    data-shine
                    style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        mixBlendMode: 'overlay',
                        background:
                            'linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.22) 50%, transparent 60%)'
                    }}
                />
            </div>

            {/* 3 · index eyebrow */}
            <div
                data-rev
                className='font-mono absolute left-6 top-6 uppercase sm:left-12 sm:top-9'
                style={{ ...revStyle, fontSize: 11, letterSpacing: '0.32em', color: 'rgba(255,255,255,0.65)' }}
            >
                {`Riffle — Nº${nn} / ${total}`}
            </div>

            {/* 4 · editorial copy block */}
            <div className='absolute inset-x-0 bottom-0 px-6 pb-[max(4.25rem,calc(env(safe-area-inset-bottom,0px)+1.5rem))] sm:px-12 sm:pb-14'>
                    <div
                        data-rev
                        className='font-mono uppercase'
                        style={{ ...revStyle, fontSize: 12, letterSpacing: '0.36em', color: 'rgba(255,255,255,0.75)' }}
                    >
                        {product.artist}
                    </div>

                    <h2
                        className='font-playfair'
                        style={{
                            overflow: 'hidden',
                            fontSize: 'clamp(2.5rem, 8vw, 6.5rem)',
                            lineHeight: 0.96,
                            letterSpacing: '-0.015em',
                            marginTop: 8
                        }}
                    >
                        <span
                            ref={titleRef}
                            data-title
                            style={{ display: 'inline-block', paddingBottom: '0.08em', willChange: 'transform' }}
                        >
                            {product.title}
                        </span>
                    </h2>

                    <div className='mt-3 flex flex-col gap-8 sm:mt-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6'>
                        {/* description + specs */}
                        <div className='sm:max-w-[60%]'>
                            <p
                                data-rev
                                className='text-[13.5px] leading-[1.65] sm:text-[14.5px] sm:leading-[1.7]'
                                style={{ ...revStyle, maxWidth: 540, color: 'rgba(255,255,255,0.8)' }}
                            >
                                {product.detail || product.description}
                            </p>

                            <div
                                data-rev
                                className='mt-4 flex flex-wrap gap-x-6 gap-y-3 sm:mt-5 sm:gap-x-9'
                                style={revStyle}
                            >
                                {product.specs.map((spec) => (
                                    <div key={spec.label}>
                                        <div
                                            className='font-mono uppercase'
                                            style={{ fontSize: 10, letterSpacing: '0.26em', opacity: 0.45 }}
                                        >
                                            {spec.label}
                                        </div>
                                        <div
                                            className='tracking-tight'
                                            style={{ fontSize: 14, opacity: 0.95, marginTop: 4 }}
                                        >
                                            {spec.value}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* price + order */}
                        <div className='flex items-center gap-8 sm:shrink-0 sm:gap-6'>
                            <div data-rev style={{ ...revStyle, position: 'relative', lineHeight: 1 }}>
                                <div
                                    className='font-mono absolute bottom-full mb-1.5 uppercase sm:static sm:mb-0'
                                    style={{ fontSize: 10, letterSpacing: '0.26em', opacity: 0.45 }}
                                >
                                    Price
                                </div>
                                <div
                                    className='font-playfair tracking-tight sm:mt-1.5'
                                    style={{ fontSize: 26 }}
                                >
                                    {product.price}
                                </div>
                            </div>

                            <button
                                ref={orderRef}
                                type='button'
                                onClick={onOrder}
                                aria-label={`Order ${product.title}`}
                                className='riffle-order rounded-full px-6 py-3.5 text-[13px] font-semibold tracking-wide sm:px-8 sm:py-4'
                                style={{
                                    background: orderBg,
                                    color: '#0a0a0c',
                                    boxShadow: orderShadow,
                                    opacity: 0
                                }}
                            >
                                <span
                                    className='riffle-order-sheen pointer-events-none'
                                    aria-hidden
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background:
                                            'linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%)'
                                    }}
                                />
                                {/* default label stays in flow to define width */}
                                <span
                                    style={{
                                        position: 'relative',
                                        opacity: ordered ? 0 : 1,
                                        transition: 'opacity 200ms ease'
                                    }}
                                >
                                    Order now →
                                </span>
                                {/* confirmed label crossfades over it */}
                                <span
                                    aria-hidden
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: ordered ? 1 : 0,
                                        transition: 'opacity 200ms ease'
                                    }}
                                >
                                    Ordered ✓
                                </span>
                            </button>
                        </div>
                    </div>
            </div>

            {/* 5 · close */}
            <button
                ref={closeRef}
                type='button'
                onClick={close}
                aria-label='Close detail'
                className='riffle-close absolute right-5 top-5 grid place-items-center sm:right-10 sm:top-8'
                style={{
                    width: 44,
                    height: 44,
                    borderRadius: 9999,
                    border: '1px solid rgba(255,255,255,0.45)',
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    boxShadow: '0 8px 24px -8px rgba(0,0,0,0.6)',
                    opacity: 0
                }}
            >
                <svg width='17' height='17' viewBox='0 0 17 17' fill='none' aria-hidden>
                    <path d='M2 2 L15 15 M15 2 L2 15' stroke='#fff' strokeWidth='2.4' strokeLinecap='round' />
                </svg>
            </button>
        </div>
    )
}

export default DetailView
